import cx from "classnames";
/* eslint-disable max-lines */
import React from "react";
import { useHistory } from "react-router-dom";

import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { ViewButton } from "../";
import { GameConceptIdSearchParam } from "../../helpers/dataFetchingHelpers";
import { GameConceptApplication } from "../../helpers/gameConceptHelpers";
import { getDashboardUrl } from "../../helpers/navigationHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmGameConceptStatus } from "../../models/crmStatus";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import * as styles from "./AaaGameConceptsList.module.css";

type GameConceptRowProps = Readonly<{
  programDashboardRouteName: RouteNames;
  gameConcept: GameConceptApplication;
  rowKey: number;
  className?: string;
}>;

export const GameConceptRow = ({
  gameConcept,
  programDashboardRouteName,
  rowKey,
  className,
}: GameConceptRowProps) => {
  const [
    untitledGame,
    draft,
    underReview,
    additionalContractsSent,
    rejected,
    approved,
    continueApplication,
    needMoreInformation,
  ] = useStrings(
    "untitledGame",
    "draft",
    "underReview",
    "additionalContractsSent",
    "rejected",
    "approved",
    "continueApplication",
    "needMoreInformation"
  );
  const history = useHistory();

  const gameConceptInfo = gameConcept.gameConceptInfo;
  const gameState = gameConceptInfo.state;
  const gameStatus = gameState.status;
  const gameTitle = gameConceptInfo.details.gameTitle;

  let statusMessage = draft;
  switch (gameStatus) {
    case CrmGameConceptStatus.UnderReview:
      statusMessage = underReview;
      break;
    case CrmGameConceptStatus.AdditionalContractsSent:
      statusMessage = additionalContractsSent;
      break;
    case CrmGameConceptStatus.Accepted:
      statusMessage = approved;
      break;
    case CrmGameConceptStatus.Rejected:
      statusMessage = rejected;
      break;
    case CrmGameConceptStatus.MoreInfoNeeded:
      statusMessage = needMoreInformation;
      break;
  }

  const viewOnlyGame =
    gameStatus === CrmGameConceptStatus.UnderReview ||
    gameStatus === CrmGameConceptStatus.AdditionalContractsSent ||
    gameStatus === CrmGameConceptStatus.Accepted ||
    gameStatus === CrmGameConceptStatus.Rejected;

  const continueButtonOnClick = () => {
    logPageAction(OnboardingPageAction.ContinueApplicationButton);
    // TODO: Investigate how to make this work client-side without breaking Fast-Track
    // and other edge cases
    history.push(getDashboardUrl(programDashboardRouteName, gameState.id));
  };

  const viewApplicationLinkOnClick = () => {
    logPageAction(OnboardingPageAction.ViewGameConceptLink);
    history.push({
      pathname: OnboardingUrls[RouteNames.AaaFormPreview],
      search: `?${GameConceptIdSearchParam}=${gameConcept.id}`,
    });
  };

  return (
    <div
      key={rowKey}
      className={cx(commonStyles.rowContainer, rowKey === 0 ? styles.firstGameRow : "", className)}
    >
      <div className={commonStyles.titleStatusContainer}>
        <h3 className={commonStyles.gameTitle}>{gameTitle !== "" ? gameTitle : untitledGame}</h3>
        <div className={styles.applicationState}>{statusMessage}</div>
      </div>
      {!viewOnlyGame && (
        <BasicButton
          as="button"
          buttonType={ButtonType.Secondary}
          className={styles.navigationButton}
          onClick={continueButtonOnClick}
          role="button"
          textDecorationUnderline={true}
        >
          {statusMessage === needMoreInformation ? needMoreInformation : continueApplication}
        </BasicButton>
      )}
      {viewOnlyGame && <ViewButton onClick={viewApplicationLinkOnClick} />}
    </div>
  );
};
